import { useEffect, useRef, useState } from "react";
import { listClientInboxes, listMailAccounts, readMail, sendClientMail, type MailAccount, type MailBody, type MailHit } from "./mail";
import { Modal } from "./Modal";

export function MailBlock({ emails, onSending }: { emails: string[]; onSending?: (busy: boolean) => void }) {
  const list = [...new Set(emails.map((item) => item.trim()).filter((item) => item.includes("@")))];
  const [chosen, setChosen] = useState(list[0] || "");
  const [rows, setRows] = useState<MailHit[]>([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<MailHit | null>(null);
  const [body, setBody] = useState<MailBody | null>(null);
  const [compose, setCompose] = useState(false);
  const [loading, setLoading] = useState(false);
  const key = list.join("|");

  useEffect(() => {
    setChosen(list[0] || "");
    setRows([]);
    setOpen(null);
    setError("");
    if (list.length === 0) return;
    let alive = true;
    setLoading(true);
    void listClientInboxes(list)
      .then((hits) => {
        if (alive) setRows(hits);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Réception indisponible");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [key]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBody(null);
    void readMail(open.accountId, open.uid)
      .then((next) => {
        if (alive) setBody(next);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Message illisible");
      });
    return () => {
      alive = false;
    };
  }, [open]);

  return (
    <div className="relative flex h-full min-h-36 flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        {list.length > 1 ? (
          <select className="crm-field h-8 min-w-0 flex-1 rounded-lg border px-2 text-xs" value={chosen} onChange={(event) => setChosen(event.target.value)}>
            {list.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        ) : <p className="truncate text-xs text-slate-500">{list[0] || "Pas d’e-mail sur la fiche"}</p>}
        <button type="button" className="crm-pill shrink-0 rounded-full px-3 py-1 text-xs font-semibold" disabled={!chosen} onClick={() => setCompose(true)}>
          Nouveau mail
        </button>
      </div>
      <div className="crm-scroll max-h-72 min-h-0 flex-1 overflow-auto">
        {loading ? <MailSkeleton /> : null}
        {!loading && list.length > 0 && rows.length === 0 && !error ? <p className="text-sm text-slate-500">Aucun message reçu de ces adresses.</p> : null}
        {rows.map((row) => (
          <button key={`${row.accountId}-${row.uid}`} type="button" className="crm-rule flex w-full items-start gap-2.5 border-b px-1 py-2.5 text-left last:border-b-0 hover:bg-black/5" onClick={() => setOpen(row)}>
            <MailGlyph />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold">{row.subject}</span>
              <span className="mt-0.5 block truncate text-[11px] text-slate-500">{row.from} · {mailWhen(row.date)}</span>
            </span>
          </button>
        ))}
        {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      </div>
      {open ? (
        <Modal onClose={() => setOpen(null)} sheetClass="crm-pop flex h-[68vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Réception</p>
              <h2 className="mt-1 truncate text-lg font-semibold">{body?.subject || open.subject}</h2>
              <p className="truncate text-xs text-slate-500">{body?.from || open.from}</p>
              <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200">
                {body?.html ? (
                  <iframe title="Message" className="h-full w-full bg-white" sandbox="" srcDoc={body.html} />
                ) : (
                  <p className="whitespace-pre-wrap p-3 text-sm">{body?.text || "Ouverture…"}</p>
                )}
              </div>
            </>
          )}
        </Modal>
      ) : null}
      {compose ? <ComposeSheet email={chosen} onClose={() => setCompose(false)} onError={setError} onSending={onSending} /> : null}
    </div>
  );
}

function ComposeSheet({ email, onClose, onError, onSending }: { email: string; onClose: () => void; onError: (message: string) => void; onSending?: (busy: boolean) => void }) {
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [accountId, setAccountId] = useState(0);
  const [subject, setSubject] = useState("");
  const [localError, setLocalError] = useState("");
  const sent = useRef(false);

  useEffect(() => {
    void listMailAccounts()
      .then((rows) => {
        setAccounts(rows);
        setAccountId(rows.find((row) => row.isDefault)?.id || rows[0]?.id || 0);
      })
      .catch((reason: unknown) => setLocalError(reason instanceof Error ? reason.message : "Comptes indisponibles"));
  }, []);

  function send(close: () => void) {
    if (sent.current) return;
    const root = document.getElementById("crm-mail-body");
    const html = root?.innerHTML.trim() || "";
    if (!accountId || !email.trim() || !subject.trim() || html === "" || html === "<br>") return;
    const payload = { accountId, to: email.trim(), subject: subject.trim(), html };
    sent.current = true;
    close();
    onSending?.(true);
    void sendClientMail(payload)
      .catch((reason: unknown) => onError(reason instanceof Error ? reason.message : "Envoi impossible"))
      .finally(() => onSending?.(false));
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-lg rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Nouveau mail</p>
          <h2 className="mt-1 text-lg font-semibold">Écrire à {email}</h2>
          <label className="mt-3 block text-xs font-semibold text-slate-500">Compte</label>
          <select className="crm-field mt-1 w-full rounded-xl border px-3 py-2 text-sm" value={accountId} onChange={(event) => setAccountId(Number(event.target.value))}>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>{account.displayName || account.email}</option>
            ))}
          </select>
          <label className="mt-3 block text-xs font-semibold text-slate-500">Objet</label>
          <input className="crm-field mt-1 w-full rounded-xl border px-3 py-2 text-sm" value={subject} onChange={(event) => setSubject(event.target.value)} />
          <div className="mt-3 flex gap-1">
            <FormatButton command="bold" label="B" />
            <FormatButton command="italic" label="I" />
            <FormatButton command="underline" label="U" />
          </div>
          <div
            id="crm-mail-body"
            contentEditable
            role="textbox"
            aria-label="Message"
            className="crm-field mt-2 min-h-32 w-full rounded-xl border px-3 py-2 text-sm outline-none"
            data-placeholder="Votre message…"
          />
          {localError ? <p className="mt-2 text-sm text-rose-700">{localError}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="crm-pill rounded-full px-4 py-1.5 text-sm font-semibold" onClick={close}>Annuler</button>
            <button type="button" className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white" onClick={() => send(close)}>
              Envoyer
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function FormatButton({ command, label }: { command: string; label: string }) {
  return (
    <button
      type="button"
      className="crm-pill grid h-7 w-7 place-items-center rounded-lg text-xs font-semibold"
      onMouseDown={(event) => {
        event.preventDefault();
        document.execCommand(command);
      }}
    >
      {label}
    </button>
  );
}

function MailSkeleton() {
  return (
    <div role="status" aria-label="Chargement des messages">
      {[0, 1, 2].map((index) => (
        <div key={index} className="crm-mail-skel-row" style={{ animationDelay: `${index * 0.08}s` }}>
          <span className="crm-mail-skel-dot" />
          <span className="min-w-0 flex-1">
            <span className="crm-mail-skel-bar w-[62%]" />
            <span className="crm-mail-skel-bar mt-2 w-[38%]" />
          </span>
        </div>
      ))}
    </div>
  );
}

function MailGlyph() {
  return (
    <span className="crm-fill grid h-8 w-8 shrink-0 place-items-center rounded-full text-white">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7.5h16v9H4v-9Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 8 7.5 5.5L19.5 8" />
      </svg>
    </span>
  );
}

function mailWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}
